import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import EmployerSidebar, { ChevronDownIcon } from './EmployerSidebar';

function BarRow({ label, count, max }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-[#1E1033] truncate">{label}</span>
        <span className="font-semibold text-[#9333EA] shrink-0 ml-2">{count}</span>
      </div>
      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#7C3AED] to-[#DB2777] rounded-full"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function DistributionCard({ title, subtitle, items, labelKey, countKey, emptyText }) {
  const max = items.length > 0 ? Math.max(...items.map((i) => i[countKey])) : 0;
  return (
    <div className="bg-white rounded-xl shadow p-5 sm:p-6">
      <h3 className="font-bold text-[#1E1033] mb-1">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 mb-4">{subtitle}</p>}
      {items.length === 0 ? (
        <p className="text-gray-500 text-sm">{emptyText || 'No data yet.'}</p>
      ) : (
        items.map((item, i) => (
          <BarRow
            key={item[labelKey] ?? i}
            label={item[labelKey]}
            count={item[countKey]}
            max={max}
          />
        ))
      )}
    </div>
  );
}

function CandidateAnalytics() {
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(10);

  const [exporting, setExporting] = useState('');
  const [exportMsg, setExportMsg] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Mobile-only: sidebar drawer toggle (same pattern as other employer pages)
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  const navigate = useNavigate();

  const fetchAnalytics = useCallback(async (limitValue) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/analytics/candidate', { params: { limit: limitValue } });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load candidate analytics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchAnalytics(limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLimitChange = (e) => {
    const value = Number(e.target.value);
    setLimit(value);
    fetchAnalytics(value);
  };

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    setExporting('pdf');
    setExportMsg('');
    try {
      const res = await api.get('/analytics/export/pdf', {
        params: { type: 'candidate' },
        responseType: 'blob',
      });
      triggerDownload(res.data, `analytics-candidate-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch {
      setExportMsg('Could not generate PDF report.');
    } finally {
      setExporting('');
      setShowExportMenu(false);
    }
  };

  const handleExportExcel = async (format) => {
    setExporting(format);
    setExportMsg('');
    try {
      const res = await api.get('/analytics/export/excel', {
        params: { type: 'candidate', format },
        responseType: 'blob',
      });
      const ext = format === 'csv' ? 'csv' : 'xlsx';
      triggerDownload(res.data, `analytics-candidate-${new Date().toISOString().slice(0, 10)}.${ext}`);
    } catch {
      setExportMsg(`Could not generate ${format.toUpperCase()} report.`);
    } finally {
      setExporting('');
      setShowExportMenu(false);
    }
  };

  const handleEmailReport = async () => {
    setExporting('email');
    setExportMsg('');
    try {
      const res = await api.post('/analytics/export/email', { type: 'candidate' });
      setExportMsg(res.data.message || 'Report emailed successfully.');
    } catch (err) {
      setExportMsg(err.response?.data?.message || 'Could not email report.');
    } finally {
      setExporting('');
      setShowExportMenu(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FF] to-[#FDF2F8]">
      <Navbar user={user} />

      <div className="flex">
        <EmployerSidebar user={user} activeTab="reports" />

       <div className="flex-1 min-w-0 lg:ml-16 lg:peer-hover:ml-64 transition-all duration-200 ease-in-out">
          <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
            {/* Mobile-only: nav drawer */}
            <button
              onClick={() => setShowFiltersMobile((s) => !s)}
              className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#1E1033]"
            >
              <span>☰ Menu</span>
              <span className="text-sm text-gray-400 inline-flex items-center gap-1">
                {showFiltersMobile ? 'Hide' : 'Show'}
                <ChevronDownIcon className={`transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`} />
              </span>
            </button>

            {showFiltersMobile && <EmployerSidebar user={user} activeTab="reports" mobile />}

            <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#EC4899] bg-clip-text text-transparent inline-block">Candidate Analytics</h2>
                <p className="text-gray-500 text-sm">Platform-wide candidate distribution</p>
              </div>

              <div className="flex items-center gap-2">
                <select
                  value={limit}
                  onChange={handleLimitChange}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#9333EA]"
                >
                  <option value={5}>Top 5</option>
                  <option value={10}>Top 10</option>
                  <option value={20}>Top 20</option>
                </select>

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowExportMenu((v) => !v)}
                    className="bg-white border border-gray-200 text-[#1E1033] px-4 py-2 rounded-lg font-semibold text-sm shadow hover:bg-purple-50 flex items-center gap-2"
                  >
                    Export
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {showExportMenu && (
                    <div className="absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-10">
                      <button
                        onClick={handleExportPdf}
                        disabled={exporting !== ''}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {exporting === 'pdf' ? 'Generating…' : 'Download PDF'}
                      </button>
                      <button
                        onClick={() => handleExportExcel('xlsx')}
                        disabled={exporting !== ''}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {exporting === 'xlsx' ? 'Generating…' : 'Download Excel'}
                      </button>
                      <button
                        onClick={() => handleExportExcel('csv')}
                        disabled={exporting !== ''}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {exporting === 'csv' ? 'Generating…' : 'Download CSV'}
                      </button>
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={handleEmailReport}
                        disabled={exporting !== ''}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {exporting === 'email' ? 'Sending…' : 'Email me this report'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {exportMsg && (
              <div className="bg-purple-50 text-[#9333EA] border border-purple-100 p-3 rounded-lg mb-4 text-sm mt-3">
                {exportMsg}
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-600 border border-red-100 p-3 rounded-lg mb-4 text-sm mt-4">
                {error}
              </div>
            )}

            {loading ? (
              <p className="text-gray-500 text-sm mt-4">Loading...</p>
            ) : data ? (
              <div className="space-y-6 mt-4">
                {/* Experience + City side by side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DistributionCard
                    title="Experience Distribution"
                    items={data.experience_distribution}
                    labelKey="range"
                    countKey="count"
                  />
                  <DistributionCard
                    title="City-wise Candidates"
                    items={data.city_wise}
                    labelKey="city"
                    countKey="count"
                  />
                </div>

                {/* Salary Expectations */}
                <div className="bg-white rounded-xl shadow p-5 sm:p-6">
                  <h3 className="font-bold text-[#1E1033] mb-1">Salary Expectations</h3>
                  {!data.salary_expectations ? (
                    <p className="text-gray-500 text-sm mt-3">No salary data yet.</p>
                  ) : (
                    <>
                      <p className="text-xs text-gray-500 mb-4">
                        Across {data.salary_expectations.min !== undefined ? 'all candidates with expected salary set' : ''}
                      </p>
                      <div className="grid grid-cols-3 gap-4 mb-5">
                        <div>
                          <p className="text-xs text-gray-500">Min</p>
                          <p className="text-lg font-bold text-[#1E1033]">
                            ₹{Number(data.salary_expectations.min).toLocaleString('en-IN')}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Median</p>
                          <p className="text-lg font-bold text-[#1E1033]">
                            ₹{Number(data.salary_expectations.median).toLocaleString('en-IN')}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Max</p>
                          <p className="text-lg font-bold text-[#1E1033]">
                            ₹{Number(data.salary_expectations.max).toLocaleString('en-IN')}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">Average: ₹{Number(data.salary_expectations.avg).toLocaleString('en-IN')}</p>
                      <div className="border-t pt-4 mt-2">
                        {data.salary_expectations.quartile_buckets.map((b) => (
                          <div key={b.range} className="flex items-center justify-between text-sm py-1">
                            <span className="text-gray-600">{b.range}</span>
                            <span className="font-medium text-[#9333EA]">
                              up to ₹{Number(b.up_to).toLocaleString('en-IN')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Skills + Education side by side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DistributionCard
                    title="Skill Distribution"
                    subtitle={`Top ${limit} most common skills`}
                    items={data.skill_distribution}
                    labelKey="label"
                    countKey="count"
                  />
                  <DistributionCard
                    title="Education Statistics"
                    items={data.education_distribution}
                    labelKey="label"
                    countKey="count"
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CandidateAnalytics;